import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  Download,
  Users,
  TrendingUp,
  Building2,
  Mail,
  FileText,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronRight,
  MapPin,
  Tag,
  BarChart3,
  PieChartIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1'];

/**
 * StatCard - Reusable metric display card
 */
const StatCard = ({ title, value, icon: Icon, description, trend }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {description && (
            <p className="text-xs text-gray-400 mt-1">{description}</p>
          )}
        </div>
        <div className="bg-orange-100 p-3 rounded-full">
          <Icon className="w-6 h-6 text-orange-600" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center text-sm">
          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
          <span className="text-green-600">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

/**
 * AdminAnalyticsPage - Dashboard for analytics and engagement metrics
 */
export default function AdminAnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [topEvents, setTopEvents] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, subsRes, eventsRes, funnelRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/subscriptions`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/top-events`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/funnel`, { headers })
      ]);

      setOverview(overviewRes.data);
      setSubscriptions(subsRes.data);
      setTopEvents(eventsRes.data);
      setFunnel(funnelRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const sendDigest = async (digestType) => {
    try {
      setSendingDigest(true);
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/digest/send?digest_type=${digestType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${digestType.charAt(0).toUpperCase() + digestType.slice(1)} digest sent successfully!`);
    } catch (err) {
      console.error('Error sending digest:', err);
      alert('Failed to send digest. Please try again.');
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
        <span className="ml-3 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-sm text-gray-500">Calendar engagement, events, and organiser metrics</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendDigest('weekly')}
                disabled={sendingDigest}
              >
                <Mail className={`w-4 h-4 mr-2 ${sendingDigest ? 'animate-pulse' : ''}`} />
                Send Weekly Digest
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAnalytics}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Link to="/admin/events">
                <Button variant="outline" size="sm">
                  Manage Events
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total ICS Downloads"
              value={overview.calendar.totalIcsDownloads}
              icon={Download}
              description="Calendar feed accesses"
            />
            <StatCard
              title="Unique Subscribers"
              value={overview.calendar.uniqueSubscribers}
              icon={Users}
              description="Tracked email subscriptions"
            />
            <StatCard
              title="Total Events"
              value={overview.events.total}
              icon={Calendar}
              description={`${overview.events.upcoming} upcoming, ${overview.events.past} past`}
            />
            <StatCard
              title="Group Enquiries"
              value={overview.engagement.groupEnquiries}
              icon={Mail}
              description="Accommodation requests"
            />
          </div>
        )}

        {/* Main Dashboard Tabs */}
        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Calendar Engagement
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <PieChartIcon className="w-4 h-4" />
              Events Performance
            </TabsTrigger>
            <TabsTrigger value="funnel" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Organiser Funnel
            </TabsTrigger>
          </TabsList>

          {/* Calendar Engagement Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Top Cities Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    Top Subscribed Cities
                  </CardTitle>
                  <CardDescription>Most popular city calendar feeds</CardDescription>
                </CardHeader>
                <CardContent>
                  {subscriptions?.topCities?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={subscriptions.topCities} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={100} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No city subscription data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Tags Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-blue-500" />
                    Top Subscribed Tags
                  </CardTitle>
                  <CardDescription>Most popular sector interests</CardDescription>
                </CardHeader>
                <CardContent>
                  {subscriptions?.topTags?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={subscriptions.topTags}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="name"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {subscriptions.topTags.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No tag subscription data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Subscriptions Table */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Subscriptions</CardTitle>
                <CardDescription>Latest calendar feed subscribers</CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptions?.recentSubscriptions?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Email</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Filters</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Events</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Accesses</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Last Access</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.recentSubscriptions.slice(0, 10).map((sub, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2 font-medium">{sub.email}</td>
                            <td className="py-3 px-2">
                              <div className="flex flex-wrap gap-1">
                                {sub.filters?.city && (
                                  <Badge variant="secondary" className="text-xs">
                                    {sub.filters.city}
                                  </Badge>
                                )}
                                {sub.filters?.venue && (
                                  <Badge variant="outline" className="text-xs">
                                    {sub.filters.venue}
                                  </Badge>
                                )}
                                {sub.filters?.tags?.map((tag, j) => (
                                  <Badge key={j} className="text-xs bg-blue-100 text-blue-800">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 px-2">{sub.eventCount}</td>
                            <td className="py-3 px-2">{sub.accessCount}</td>
                            <td className="py-3 px-2 text-gray-500">
                              {new Date(sub.lastAccessedAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No subscriptions recorded yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Performance Tab */}
          <TabsContent value="events" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Events by City */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    Events by City
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topEvents?.byCity?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={topEvents.byCity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No event data</p>
                  )}
                </CardContent>
              </Card>

              {/* Events by Venue */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Events by Venue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topEvents?.byVenue?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={topEvents.byVenue.slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No venue data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Events by Enquiries */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Top Events by Group Enquiries
                </CardTitle>
                <CardDescription>Events generating the most accommodation requests</CardDescription>
              </CardHeader>
              <CardContent>
                {topEvents?.topByEnquiries?.length > 0 ? (
                  <div className="space-y-3">
                    {topEvents.topByEnquiries.map((event, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{event.name}</span>
                        <Badge className="bg-green-100 text-green-800">
                          {event.count} enquiries
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No enquiry data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Next events on the calendar</CardDescription>
              </CardHeader>
              <CardContent>
                {topEvents?.upcomingEvents?.length > 0 ? (
                  <div className="space-y-2">
                    {topEvents.upcomingEvents.map((event, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{event.name}</p>
                          <p className="text-sm text-gray-500">
                            {event.venueName}, {event.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-orange-600">
                            {event.dates?.display || event.dates?.start}
                          </p>
                          <p className="text-xs text-gray-400">{event.organiserName}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No upcoming events</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organiser Funnel Tab */}
          <TabsContent value="funnel" className="space-y-6">
            {/* Funnel Stats */}
            {overview && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-900">{overview.organiserFunnel.submissions}</p>
                    <p className="text-sm text-blue-700">Event Submissions</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-900">{overview.organiserFunnel.pendingRequests}</p>
                    <p className="text-sm text-yellow-700">Pending Reviews</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-900">{overview.organiserFunnel.approvedRequests}</p>
                    <p className="text-sm text-green-700">Approved Changes</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-900">{overview.organiserFunnel.changeRequests}</p>
                    <p className="text-sm text-purple-700">Total Change Requests</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Requests by Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Change Requests by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  {funnel?.requestsByStatus?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={funnel.requestsByStatus}
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="status"
                          label={({ status, count }) => `${status}: ${count}`}
                        >
                          {funnel.requestsByStatus.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                entry.status === 'approved' ? '#10b981' :
                                entry.status === 'pending' ? '#f59e0b' :
                                entry.status === 'rejected' ? '#ef4444' : COLORS[index]
                              } 
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No change request data</p>
                  )}
                </CardContent>
              </Card>

              {/* Requests by Type */}
              <Card>
                <CardHeader>
                  <CardTitle>Change Requests by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {funnel?.requestsByType?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={funnel.requestsByType}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No type data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Event Submissions */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Event Submissions</CardTitle>
                <CardDescription>Latest events submitted by organisers</CardDescription>
              </CardHeader>
              <CardContent>
                {funnel?.recentSubmissions?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Event Name</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Organiser</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Email</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-500">Submitted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {funnel.recentSubmissions.slice(0, 10).map((sub, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2 font-medium">{sub.eventName}</td>
                            <td className="py-3 px-2">{sub.organiserName}</td>
                            <td className="py-3 px-2 text-gray-500">{sub.organiserEmail}</td>
                            <td className="py-3 px-2 text-gray-500">
                              {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No submissions yet</p>
                )}
              </CardContent>
            </Card>

            {/* Event Series */}
            <Card>
              <CardHeader>
                <CardTitle>Event Series</CardTitle>
                <CardDescription>Events with multiple editions</CardDescription>
              </CardHeader>
              <CardContent>
                {funnel?.eventSeries?.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {funnel.eventSeries.map((series, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium truncate">{series.name}</span>
                        <Badge variant="secondary">{series.editions} editions</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No series data</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
